import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import Loading from 'design-system/Loading/Loading';
import useLogout from 'hooks/services/login/useLogout';
import useIsAdmin from 'hooks/services/session/useIsAdmin';
import usePrefetchQueryServiceNoParams from 'hooks/services/usePrefetchQueryServiceNoParams';
import Error403 from 'pages/ErrorPages/403';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { authentication } from 'services/AuthService/Login/actions';
import sessionService from 'services/AuthService/Login/sessionService';
import type { ReduxDispatch, ReduxStateProps } from 'types/services/redux';
import setUserId from 'utils/logging/setUserId';

const DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN ?? '';
const CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID ?? '';

const mapStateToProps = (state: ReduxStateProps) => ({
  authAction: state.authReducer.loginReducer.authAction,
  authStatus: state.authReducer.loginReducer.authStatus,
});

const mapDispatchToProps = (dispatch: ReduxDispatch) => ({
  authenticate: authentication(dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type AuthProviderProps = PropsWithChildren<ConnectedProps<typeof connector>>;

type AuthParams = { redirect_uri: string; audience: string; screen_hint?: string; login_hint?: string };

const onRedirectCallback = async () => {
  const loginRedirect = localStorage.getItem('redirect') ?? window.location.origin;
  localStorage.removeItem('redirect');
  localStorage.removeItem('screenhint');

  await window.history.replaceState({}, document.title, loginRedirect);
};

/**
 * Sanitizes an email address.
 */
function sanitizeEmail(email: string | null): string | undefined {
  if (!email) {
    return undefined;
  }

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  return emailRegex.test(email) ? email : undefined;
}

function getAuthParams(): AuthParams {
  const queryParams = new URLSearchParams(window.location.search);
  const email = sanitizeEmail(queryParams.get('email'));

  return {
    redirect_uri: window.location.origin,
    audience: 'https://i3d.net',
    screen_hint: localStorage.getItem('screenhint') ?? undefined,
    login_hint: email,
  };
}

const Internal = ({ children, authenticate, authStatus, authAction }: AuthProviderProps) => {
  const { isLoading, isAuthenticated, logout, getAccessTokenSilently, loginWithRedirect, error } = useAuth0();
  const prefetchService = usePrefetchQueryServiceNoParams();
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        // ensure not authenticated
        authenticate(false);
      } else {
        // get access token
        getAccessTokenSilently().then((apiToken) => {
          // ensure authenticated
          authenticate(true, apiToken);
          // start fetch user details
          prefetchService(sessionService.getUser);
        });
      }
    }
  }, [authenticate, getAccessTokenSilently, isAuthenticated, isLoading, prefetchService]);

  useEffect(() => {
    if (authAction === 'login') {
      // redirect to login
      loginWithRedirect();
    }
  }, [authAction, loginWithRedirect]);

  useEffect(() => {
    if (authAction === 'logout') {
      // reset user id on newRelic
      setUserId(null);
      // trigger auth0 logout
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  }, [authAction, logout]);

  useEffect(() => {
    if (error) {
      setHasAccess(false);
    }
  }, [error]);

  if (authStatus === 'init') {
    // loading
    return <Loading />;
  }

  if (!hasAccess) {
    return <Error403 />;
  }

  // loaded and authenticated
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

/**
 * Authentication provider
 */
const AuthProvider = ({ children, authenticate, authAction, authStatus }: AuthProviderProps) => {
  const { data: isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const { mutate: logout } = useLogout();
  const [authParams, setAuthParams] = useState<AuthParams | null>(null);

  useEffect(() => {
    setAuthParams(getAuthParams());
  }, []);

  useEffect(() => {
    if (isAdmin?.isAdmin) {
      authenticate(true);
    }
  }, [authenticate, isAdmin]);

  useEffect(() => {
    if (isAdmin?.isAdmin && authAction === 'logout') {
      // reset user id on newRelic
      setUserId(null);
      // trigger one api logout
      logout();
    }
  }, [authAction, isAdmin, logout]);

  if (isAdminLoading || !authParams) {
    return <Loading />;
  }

  if (isAdmin?.isAdmin) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return (
    <Auth0Provider domain={DOMAIN} clientId={CLIENT_ID} authorizationParams={authParams} onRedirectCallback={onRedirectCallback}>
      <Internal authAction={authAction} authStatus={authStatus} authenticate={authenticate}>
        {children}
      </Internal>
    </Auth0Provider>
  );
};

export default connector(AuthProvider);
